// Define API URL
const API_URL = `${process.env.REACT_APP_API_URL}/api`;

interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  profileImage?: string;
  profilePicture?: string;
  role: string;
  timeZone: string;
  customUrl?: string;
  username?: string;
  preferredLanguage?: 'en' | 'ja';
  // Profile settings
  country?: string;
  dateFormat?: string;
  timeFormat?: string;
  // Professional information
  title?: string; // Database field is 'title', not 'jobTitle'
  jobTitle?: string; // Keep for compatibility
  department?: string;
  company?: string;
  // Branding settings
  organizationName?: string;
  coverPhoto?: string;
  coverPhotoMobile?: string;
  hideSailLabBranding?: boolean;
  brandColor?: string;
  [key: string]: any;
}

/**
 * Get auth header from localStorage token
 */
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token 
    ? { Authorization: `Bearer ${token}` }
    : {};
};

/**
 * User Service
 * 
 * Provides methods to interact with user API endpoints
 */
const userService = {
  /**
   * Get the current logged in user
   */
  getCurrentUser: () => {
    return fetch(`${API_URL}/users/me`, {
      headers: getAuthHeader()
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to get current user');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Get all users (admin only)
   */
  getAllUsers: () => {
    return fetch(`${API_URL}/users`, {
      headers: getAuthHeader()
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to get users');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Update user profile
   */
  updateUser: (id: string, userData: Partial<User>) => {
    return fetch(`${API_URL}/users/me`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to update user profile');
        } else {
          await res.text(); // Read the response but don't use it
          throw new Error('Server error: ' + res.status);
        }
      }
      const data = await res.json();
      return data;
    });
  },

  /**
   * Upload profile image
   */
  uploadProfileImage: (id: string, formData: FormData) => {
    return fetch(`${API_URL}/users/me/profile-image`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to upload profile image');
        } else {
          await res.text(); // Read the response but don't use it
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  // Note: Organization logo upload moved to organizationService.uploadLogo()

  /**
   * Upload cover photo
   */
  uploadCoverPhoto: (id: string, formData: FormData) => {
    return fetch(`${API_URL}/users/me/cover-photo`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to upload cover photo');
        } else {
          await res.text();
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Delete user account
   */
  deleteAccount: (id: string, password: string) => {
    return fetch(`${API_URL}/users/me`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password })
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to delete account');
        } else {
          await res.text(); // Read the response but don't use it
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Get a user by username or email
   */
  getUserByUsername: (username: string) => {
    return fetch(`${API_URL}/users/username/${username}`, {
      headers: getAuthHeader()
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to get user by username');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Update user profile (simplified method for language context)
   */
  updateProfile: (profileData: Partial<User>, token?: string) => {
    const headers = token
      ? { Authorization: `Bearer ${token}` }
      : getAuthHeader();

    return fetch(`${API_URL}/users/me`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(profileData)
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to update profile');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Delete profile image from server and Cloudflare
   */
  deleteProfileImage: () => {
    return fetch(`${API_URL}/users/me/delete-image`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ type: 'profile' })
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to delete profile image');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Delete any type of image (profile, logo, cover)
   */
  deleteImage: (type: 'profile' | 'logo' | 'cover') => {
    return fetch(`${API_URL}/users/me/delete-image`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ type })
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || `Failed to delete ${type} image`);
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  // ===== OAuth Provider Management =====

  /**
   * Get linked OAuth providers for the current user
   * Returns list of providers (Google, Microsoft) with connection status
   */
  getOAuthProviders: () => {
    return fetch(`${API_URL}/users/me/oauth-providers`, {
      headers: getAuthHeader()
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to get OAuth providers');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Unlink an OAuth provider from the account
   * @param provider - 'google' or 'microsoft'
   */
  unlinkOAuthProvider: (provider: 'google' | 'microsoft') => {
    return fetch(`${API_URL}/users/me/oauth-providers/${provider}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    }).then(async res => {
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to unlink provider');
        } else {
          throw new Error('Server error: ' + res.status);
        }
      }
      return res.json();
    });
  },

  /**
   * Get OAuth linking URL for a provider
   * Includes the JWT token as a query parameter since browser navigation doesn't send headers
   * @param provider - 'google' or 'microsoft'
   * @returns URL to redirect to for OAuth linking
   */
  getOAuthLinkUrl: (provider: 'google' | 'microsoft') => {
    const token = localStorage.getItem('token');
    return `${API_URL}/auth/link/${provider}?token=${encodeURIComponent(token || '')}`;
  },

  /**
   * Set or change the account password.
   * currentPassword is required only when a password is already set.
   */
  updatePassword: (newPassword: string, currentPassword?: string) => {
    return fetch(`${API_URL}/users/me/password`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ newPassword, currentPassword })
    }).then(async res => {
      const contentType = res.headers.get('content-type');
      const data = contentType && contentType.includes('application/json') ? await res.json() : null;
      if (!res.ok) {
        const error: any = new Error(data?.message || 'Failed to update password');
        error.code = data?.code;
        throw error;
      }
      return data;
    });
  },

  // ===== Notification Preferences =====

  /**
   * Get the current user's email notification preferences
   */
  getNotificationPreferences: () => {
    return fetch(`${API_URL}/users/me/notification-preferences`, {
      headers: getAuthHeader()
    }).then(async res => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Failed to load preferences' }));
        throw new Error(error.message);
      }
      return res.json();
    });
  },

  /**
   * Update the current user's email notification preferences
   */
  updateNotificationPreferences: (emailNotificationPreferences: {
    bookingConfirmation: boolean;
    bookingCancellation: boolean;
  }) => {
    return fetch(`${API_URL}/users/me/notification-preferences`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ emailNotificationPreferences })
    }).then(async res => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Failed to update preferences' }));
        throw new Error(error.message);
      }
      return res.json();
    });
  }
};

export default userService; 